import { useEffect, useRef, useState } from 'react';

interface AdaptivePollingOptions {
  activeMs: number;
  idleMs: number;
  idleAfterMs?: number;
}

const DEFAULT_IDLE_AFTER_MS = 30_000;

export const useAdaptivePolling = (options: AdaptivePollingOptions) => {
  const { activeMs, idleMs, idleAfterMs = DEFAULT_IDLE_AFTER_MS } = options;
  const [intervalMs, setIntervalMs] = useState(activeMs);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    const updateInterval = () => {
      const isHidden = document.visibilityState === 'hidden';
      const idleForMs = Date.now() - lastActivityRef.current;
      const nextInterval = isHidden || idleForMs >= idleAfterMs ? idleMs : activeMs;
      setIntervalMs((prev) => (prev === nextInterval ? prev : nextInterval));
    };

    const markActivity = () => {
      lastActivityRef.current = Date.now();
      updateInterval();
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'pointerdown',
    ];

    activityEvents.forEach((event) =>
      window.addEventListener(event, markActivity, { passive: true })
    );
    document.addEventListener('visibilitychange', updateInterval);

    updateInterval();
    const idleCheck = window.setInterval(updateInterval, 1000);

    return () => {
      activityEvents.forEach((event) => window.removeEventListener(event, markActivity));
      document.removeEventListener('visibilitychange', updateInterval);
      window.clearInterval(idleCheck);
    };
  }, [activeMs, idleAfterMs, idleMs]);

  return intervalMs;
};
